/* eslint-disable no-unused-vars */
import React, { useEffect, useCallback, useState } from 'react';
import { format } from 'date-fns';
import { Button, message, Modal, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import useListFilters from '../../hooks/useListFilters';
import ListPage from '../../components/core/ListPage';
import { useClinicsList } from '../../hooks/clinic/useClinicsList';
import { APP_TERMS } from '../../app-terms';
import { AvailableAppLocales } from '../../availableAppLocales';
import { useUserLocale } from '../../hooks/useLocale';
import {
  filterClinicActionsByStatus,
  getClinicStatusMetaByStatus,
} from '../../utils/clinic';
import { useNavigate } from 'react-router-dom';
import { CLINIC_STATUS_ENUM } from '../../appContants';
import { useClinicStatusUpdate } from '../../hooks/clinic/useClinicStatusUpdate';
import apiService from '../../axios/apiService';
const { Text } = Typography;
const BREAD_CRUMBS = [
  { label: 'Clinic', to: '/user/cliniclist' },
  { label: 'List', to: '/user/cliniclist' },
];
const deleteClinicConfig = {
  title: 'Delete Clinic',
  content: 'Are you sure you want to delete this Clinic?',
  okText: 'Yes',
  cancelText: 'No',
};
const ClinicList = () => {
  const { data, getList } = useClinicsList();
  const { loading: statusUpdateLoading, updateClinicStatus } =
    useClinicStatusUpdate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [hideFooter, setHideFooter] = useState(false);
  const {
    search,
    setSearch,
    pagination,
    setPagination,
    sortParams,
    setSortParams,
    loading,
    setLoading,
  } = useListFilters();
  const [modal, contextHolder] = Modal.useModal();
  const { userLocale } = useUserLocale();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  

  // Table
  const handleEdit = (record) => {
    navigate(`/user/clinic/edit?id=${record?.clinicId}`);
  };

  const handleDelete = async (record) => {
    const confirmed = await modal.confirm(deleteClinicConfig);
    if (!confirmed) return;
     setLoading(true);
    try {
      console.log(record);
      await apiService.deleteClinic(record?.clinicId);
      setLoading(false);
      reload();
    } catch (error) {
      console.log('✌️error --->', error);
      message.error('Failed to delete clinic');
      setLoading(false);
    }
  }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getList({
        statusFilter: statusFilter,
        search,
        page: pagination.current,
        limit: pagination.pageSize,
        sortOrder: sortParams.sortOrder,
        sortBy: sortParams.sortField,
      });
      setLoading(false);
      setPagination({ total: response.totalItems });
    } catch (error) {
      setLoading(false);
    }
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
    statusFilter
  ]);
  const handleTableChange = (newPagination, _, sorter) => {
    const pageSizeChanged = newPagination?.pageSize !== pagination.pageSize;
    const nextPage = pageSizeChanged ? 1 : newPagination?.current || 1;
    setSortParams({
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
    setPagination({ current: nextPage, pageSize: newPagination?.pageSize });
  };

  const onActionItemClick = async ({ key }, record) => {
    switch (key) {
      case 'Edit':
        handleEdit(record);
        break;
      case CLINIC_STATUS_ENUM.APPROVED:
      case CLINIC_STATUS_ENUM.DISAPPROVE:
      case CLINIC_STATUS_ENUM.ENABLE:
      case CLINIC_STATUS_ENUM.DISABLE:
        try {
          const { text } = getClinicStatusMetaByStatus(key);
          const confirmed = await modal.confirm({
            title: `Mark Clinic ${text}`,
            content: (
              <Text>
                Are you sure you want to mark the clinic as <b>{text}</b> ?
              </Text>
            ),
          });
          if (!confirmed) return;
          await updateClinicStatus(key, record?.clinicId);
          message.success(
            <Text>
              Clinic marked as <b>{text}</b>
            </Text>
          );
          reload();
        } catch (error) {
          console.log('Error on clinic status update');
          const { text } = getClinicStatusMetaByStatus(key);
          message.error(
            <Text>
              Failed to mark clinic as <b>{text}</b>
            </Text>
          );
        }
        break;
      case 'Delete':
        handleDelete(record);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    reload();
  }, [
    pagination.current,
    pagination.pageSize,
    sortParams.sortOrder,
    sortParams.sortField,
    search,
    statusFilter
  ]);

  const handleFilterChange = (value) => {
    setStatusFilter(value || 'all');
    setPagination({ current: 1 });
  };
  const handleSearch = (value) => {
    setSearch(value);
  };
  const columns = [
    {
      title: 'Clinic ID',
      dataIndex: 'clinicId',
    },
    {
      title: 'Clinic Name',
      dataIndex: 'title',
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Owner Email',
      dataIndex: 'ownerEmail',
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      sorter: true,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'contactNumber',
      sorter: true,
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (_, { status }, index) => {
        let statusMeta = getClinicStatusMetaByStatus(status);
        return (
          <Tag color={statusMeta?.color} key={index}>
            {statusMeta?.text}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text) =>
        format(new Date(text), APP_TERMS.date_1_format, {
          locale: AvailableAppLocales?.[userLocale],
        }),
      sorter: true,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      render: (text) =>
        format(new Date(text), APP_TERMS.date_1_format, {
          locale: AvailableAppLocales?.[userLocale],
        }),
      sorter: true,
    },
    {
      title: 'Action',
      fixed: 'right',
      dataIndex: '',
      render: (_, record) => {
        const actionItems = [...filterClinicActionsByStatus(record?.status)].filter(
          (item) => item?.key !== CLINIC_STATUS_ENUM.ENABLE && item?.key !== CLINIC_STATUS_ENUM.DISABLE
        );
        const showEnableDisableToggle =
          record?.status === CLINIC_STATUS_ENUM.APPROVED || record?.status === CLINIC_STATUS_ENUM.DISABLE;
        const isEnabled = record?.status === CLINIC_STATUS_ENUM.APPROVED;

        const handleToggleChange = async (checked) => {
          const key = checked ? CLINIC_STATUS_ENUM.ENABLE : CLINIC_STATUS_ENUM.DISABLE;
          const { text } = getClinicStatusMetaByStatus(key);
          try {
            const confirmed = await modal.confirm({
              title: `Mark Clinic ${text}`,
              content: (
                <Text>
                  Are you sure you want to mark the clinic as <b>{text}</b> ?
                </Text>
              ),
            });
            if (!confirmed) return;
            await updateClinicStatus(key, record?.clinicId);
            message.success(
              <Text>
                Clinic marked as <b>{text}</b>
              </Text>
            );
            reload();
          } catch (error) {
            message.error(
              <Text>
                Failed to mark clinic as <b>{text}</b>
              </Text>
            );
          }
        };

        return (
          <Space size="middle">
            <Tooltip title="Edit" placement="left">
              <Button icon={<i className="fa fa-edit" />} onClick={() => handleEdit(record)} />
            </Tooltip>
            {showEnableDisableToggle && (
              <Tooltip title={isEnabled ? 'Disable clinic' : 'Enable clinic'} placement="left">
                <Switch
                  // size="small"
                  checked={isEnabled}
                  checkedChildren={getClinicStatusMetaByStatus(CLINIC_STATUS_ENUM.ENABLE).text}
                  unCheckedChildren={getClinicStatusMetaByStatus(CLINIC_STATUS_ENUM.DISABLE).text}
                  onChange={handleToggleChange}
                />
              </Tooltip>
            )}
            {actionItems?.map((item) => (
              <Tooltip key={item?.key} title={item?.label} placement="left">
                <Button
                  icon={<i className={`fa ${item?.icon}`} />}
                  type="primary"
                  onClick={() => onActionItemClick(item, record)}
                />
              </Tooltip>
            ))}
            {
            record?.status === CLINIC_STATUS_ENUM.DISAPPROVE && 
            !record?.hasCases && (
                <Tooltip title="Delete" placement="left">
                  <Button
                    icon={<i className="fa fa-trash" />}
                    danger
                    onClick={() => onActionItemClick({ key: 'Delete' }, record)}
                  />
                </Tooltip>
              )}
            {contextHolder}
          </Space>
        );
      },
    },
  ];
  const activateFooter = (rows) => {
    setSelectedRows(rows);
    setHideFooter(rows.length === 0);
  };

  const handleBulkApproved = async () => {
    if (!selectedRows.length) return message.warning('Please select at least one clinic.');

    const confirmed = await modal.confirm({
      title: 'Bulk Approved Clinics',
      content: 'Are you sure you want to assign the selected clinics?',
      okText: 'Yes',
      cancelText: 'No',
    });
    if (!confirmed) return;

    const clinicIds = selectedRows
      .filter(row => row.status !== "APPROVED") // only include rows not approved
      .map(row => row.clinicId); // extract the clinicId
    try {
      setLoading(true);
      const response = await apiService.bulkApprovedClinic(clinicIds); // ✅ bulk assign API
      message.success(response?.message || 'Clinics assigned successfully');
      reload();
      setSelectedRows([]); // Clear selection
    } catch (error) {
      message.error(error?.message || 'Failed to assign selected clinics');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRows.length) return message.warning('Please select at least one clinic.');

    const confirmed = await modal.confirm(deleteClinicConfig);
    if (!confirmed) return;

    const clinicIds = selectedRows.map((row) => row.clinicId);
    try {
      setLoading(true);
      const response = await apiService.bulkDeleteClinic(clinicIds); // ✅ your bulk delete API
      message.success(response?.message || 'Clinics deleted successfully');
      reload();
      setSelectedRows([]); // clear selection
    } catch (error) {
      message.error(error?.message || 'Failed to delete selected clinics');
    } finally {
      setLoading(false);
    }
  };
    return (
    <ListPage
      title="Clinic List"
      data={data?.items || []}
      columns={columns}
      loading={loading || statusUpdateLoading}
      pagination={pagination}
      rowKey={(record) => record?.clinicId}
      onRefresh={reload}
      onSearch={handleSearch}
      onTableChange={handleTableChange}
      breadCrumbItems={BREAD_CRUMBS}
      onSelectRowsfun={activateFooter}
      selectedRowKeys={selectedRows.map((r) => r.clinicId)}
      hideAddAction
      footer={
        !hideFooter && selectedRows.length > 0 && (
          <>
            <Button type="primary" className="m-2" onClick={handleBulkApproved}>
              Approved Selected
            </Button>
            <Button type="primary" danger onClick={handleBulkDelete}>
              Delete Selected
            </Button>
          </>
        )
      }
      showApproveFilter   // 👈 new
      onshowApproveFilterChange={handleFilterChange} // 👈 new
    />
  );

};

export default ClinicList;
